import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { wayleaveDocsAPI } from '../utils/api';

// Slot status model (spec §4) — kept in sync with WayleaveDocuments.js
const SLOT_STATUS = {
  not_started: { label: 'Not Started', color: '#9ca3af' },
  submitted: { label: 'Awaiting Review', color: '#f59e0b' },
  in_review: { label: 'Under Review', color: '#3b82f6' },
  changes_requested: { label: 'Changes Requested', color: '#f97316' },
  accepted: { label: 'Accepted', color: '#10b981' },
  submitted_to_dre: { label: 'With DRE', color: '#6366f1' },
  dre_query: { label: 'DRE Query', color: '#ef4444' },
  approved: { label: 'Approved', color: '#059669' },
  not_applicable: { label: 'N/A', color: '#9ca3af' },
};
const slotMeta = (s) => SLOT_STATUS[s] || SLOT_STATUS.not_started;

const CR_STATE = {
  open: { label: 'Open', color: '#f97316' },
  addressed: { label: 'Addressed', color: '#3b82f6' },
  verified: { label: 'Verified', color: '#10b981' },
};

function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const diff = Date.now() - d.getTime();
  const days = Math.floor(diff / 86400000);
  if (days >= 1) return `${days}d ago`;
  const hrs = Math.floor(diff / 3600000);
  if (hrs >= 1) return `${hrs}h ago`;
  const mins = Math.floor(diff / 60000);
  return mins >= 1 ? `${mins}m ago` : 'just now';
}

function heldBy(status, suppliedBy) {
  if (['submitted', 'in_review'].includes(status)) return 'MPAMOT (Consultant)';
  if (['not_started', 'changes_requested', 'dre_query'].includes(status)) {
    return suppliedBy === 'consultant' ? 'MPAMOT (Consultant)' : 'Contractor';
  }
  return '—';
}

function fmtDate(d) {
  return d ? new Date(d).toLocaleString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
}

function WayleaveDocDrawer({ slotId, userInfo, onClose, onChanged }) {
  const isConsultant = ['admin', 'sysadmin'].includes((userInfo?.role || '').toLowerCase());

  const [slot, setSlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Upload
  const [file, setFile] = useState(null);
  const [note, setNote] = useState('');

  // Change request composer
  const [crKind, setCrKind] = useState('change_request');
  const [crBody, setCrBody] = useState('');
  const [crFiles, setCrFiles] = useState([]);

  // @mention autocomplete (for the composer)
  const [mentionUsers, setMentionUsers] = useState([]);
  const [showMention, setShowMention] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionCursorPos, setMentionCursorPos] = useState(null);
  const crInputRef = useRef(null);

  // Per-CR reply text + attachments
  const [replyText, setReplyText] = useState({});
  const [replyFiles, setReplyFiles] = useState({});

  // Contact Details form (doc_key === 'contact_details')
  const [contactForm, setContactForm] = useState({ fullName: '', position: '', company: '', group: '', mobile: '', altMobile: '', email: '' });

  const fetchSlot = useCallback(async () => {
    try {
      const res = await wayleaveDocsAPI.getSlot(slotId);
      setSlot(res.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load document');
    } finally {
      setLoading(false);
    }
  }, [slotId]);

  useEffect(() => { setLoading(true); fetchSlot(); }, [fetchSlot]);

  // Prefill the Contact Details form from the current revision's saved data
  useEffect(() => {
    const cur = slot?.revisions?.find((r) => !r.superseded) || slot?.revisions?.[0];
    if (cur?.form_data) {
      try {
        const d = JSON.parse(cur.form_data);
        setContactForm((f) => ({ ...f, ...d }));
      } catch { /* ignore malformed */ }
    }
  }, [slot]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const afterChange = async () => { await fetchSlot(); if (onChanged) onChanged(); };

  const doUpload = async () => {
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (note.trim()) fd.append('note', note.trim());
      await wayleaveDocsAPI.uploadRevision(slotId, fd);
      setFile(null); setNote('');
      await afterChange();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload revision');
    } finally {
      setBusy(false);
    }
  };

  const doSaveContact = async () => {
    setBusy(true);
    setError('');
    try {
      await wayleaveDocsAPI.saveContactDetails(slotId, contactForm);
      await afterChange();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save contact details');
    } finally {
      setBusy(false);
    }
  };

  const doReview = async (action) => {
    setBusy(true);
    setError('');
    try {
      await wayleaveDocsAPI.reviewSlot(slotId, action);
      await afterChange();
    } catch (err) {
      setError(err.response?.data?.error || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const addCR = async () => {
    if (!crBody.trim()) return;
    setBusy(true);
    setError('');
    try {
      await wayleaveDocsAPI.createChangeRequest(slotId, { kind: crKind, body: crBody.trim(), files: crFiles });
      setCrBody('');
      setCrFiles([]);
      await afterChange();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add');
    } finally {
      setBusy(false);
    }
  };

  const setCrState = async (crId, state) => {
    setBusy(true);
    setError('');
    try {
      await wayleaveDocsAPI.updateChangeRequest(crId, state);
      await afterChange();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update');
    } finally {
      setBusy(false);
    }
  };

  const addReply = async (crId) => {
    const body = (replyText[crId] || '').trim();
    if (!body) return;
    setBusy(true);
    setError('');
    try {
      await wayleaveDocsAPI.addChangeRequestReply(crId, body, replyFiles[crId] || []);
      setReplyText((p) => ({ ...p, [crId]: '' }));
      setReplyFiles((p) => ({ ...p, [crId]: [] }));
      await fetchSlot();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reply');
    } finally {
      setBusy(false);
    }
  };

  const ATTACH_ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';

  // Links to files attached to a change request / reply
  const renderAttachments = (list) => {
    if (!list || list.length === 0) return null;
    return (
      <div className="mt-1.5 flex flex-col items-start gap-1">
        {list.map((a) => (
          <a
            key={a.id}
            href={a.fileUrl || '#'}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 hover:underline"
          >
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
            {a.original_filename || 'attachment'}
          </a>
        ))}
      </div>
    );
  };

  // Chips for files selected but not yet uploaded, with a remove control
  const renderFileChips = (files, onRemove) => {
    if (!files || files.length === 0) return null;
    return (
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {files.map((f, i) => (
          <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] bg-gray-100 text-gray-700 rounded-full">
            <span className="truncate max-w-[140px]">{f.name}</span>
            <button type="button" onClick={() => onRemove(i)} className="text-gray-400 hover:text-red-500 leading-none text-sm">×</button>
          </span>
        ))}
      </div>
    );
  };

  // Mentionable users for @mention autocomplete
  useEffect(() => {
    axios.get('/api/users/mentionable', { withCredentials: true })
      .then((res) => setMentionUsers(res.data || []))
      .catch(() => setMentionUsers([]));
  }, []);

  const filteredMentions = () => mentionUsers.filter((u) => {
    const s = mentionFilter;
    return u.username.toLowerCase().includes(s)
      || (u.firstName && u.firstName.toLowerCase().includes(s))
      || (u.lastName && u.lastName.toLowerCase().includes(s));
  });

  const handleCrBodyChange = (e) => {
    const val = e.target.value;
    setCrBody(val);
    const before = val.slice(0, e.target.selectionStart);
    const m = before.match(/@(\S*)$/);
    if (m) {
      setMentionFilter(m[1].toLowerCase());
      setMentionCursorPos(m.index);
      setShowMention(true);
      setMentionIndex(0);
    } else {
      setShowMention(false);
    }
  };

  const insertMention = (user) => {
    const before = crBody.slice(0, mentionCursorPos);
    const afterCursor = crInputRef.current?.selectionStart ?? (mentionCursorPos + mentionFilter.length + 1);
    const after = crBody.slice(afterCursor);
    setCrBody(before + '@' + user.username + ' ' + after);
    setShowMention(false);
    setTimeout(() => {
      const pos = before.length + user.username.length + 2;
      if (crInputRef.current) { crInputRef.current.focus(); crInputRef.current.setSelectionRange(pos, pos); }
    }, 0);
  };

  const handleCrKeyDown = (e) => {
    if (!showMention) return;
    const f = filteredMentions();
    if (!f.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex((p) => (p + 1) % f.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIndex((p) => (p - 1 + f.length) % f.length); }
    else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(f[mentionIndex]); }
    else if (e.key === 'Escape') { e.preventDefault(); setShowMention(false); }
  };

  const renderWithMentions = (text) => (text || '').split(/(@\S+)/g).map((part, i) => (
    /^@\S+/.test(part)
      ? <span key={i} className="bg-amber-100 text-amber-800 px-1 rounded text-xs font-medium">{part}</span>
      : <span key={i}>{part}</span>
  ));

  const meta = slot ? slotMeta(slot.status) : null;
  const currentRev = slot?.revisions?.find((r) => !r.superseded) || slot?.revisions?.[0] || null;
  const isAppointment = slot?.doc_key === 'appointment_letter';
  const isContactDetails = slot?.doc_key === 'contact_details';
  // Server-authoritative: consultant OR the assigned contractor may write here
  const canWrite = !!slot?.canWrite;
  const canEditForm = isContactDetails && canWrite;
  const canUpload = canWrite && !isAppointment && !isContactDetails;
  const openCRs = slot?.changeRequests?.filter((c) => c.kind === 'change_request' && c.state !== 'verified').length || 0;

  return (
    <div className="fixed inset-0 z-[9999]">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="border-b border-gray-200 px-5 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-gray-900 truncate">{slot?.name || 'Document'}</h3>
              {meta && (
                <span
                  className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0"
                  style={{ backgroundColor: `${meta.color}1a`, color: meta.color }}
                >
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: meta.color }} />
                  {meta.label}
                </span>
              )}
            </div>
            {slot && (
              <p className="text-xs text-gray-500 mt-1">
                {currentRev ? `R${currentRev.revision_no}` : 'No revision'} · Held by {heldBy(slot.status, slot.supplied_by)} · Updated {timeAgo(slot.updated_at)}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700 rounded flex-shrink-0" title="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">{error}</div>}

          {loading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : !slot ? (
            <p className="text-sm text-gray-500">Document not found.</p>
          ) : (
            <>
              {/* Consultant review actions */}
              {isConsultant && (
                <div className="flex flex-wrap gap-2">
                  {slot.status === 'submitted' && (
                    <button onClick={() => doReview('start')} disabled={busy}
                      className="px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                      Start review
                    </button>
                  )}
                  {['submitted', 'in_review', 'changes_requested'].includes(slot.status) && openCRs === 0 && (
                    <button onClick={() => doReview('accept')} disabled={busy}
                      className="px-3 py-1.5 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50">
                      Accept
                    </button>
                  )}
                  {slot.status === 'accepted' && (
                    <span className="text-sm text-emerald-700 font-medium flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      Accepted
                    </span>
                  )}
                  {openCRs > 0 && (
                    <span className="text-xs text-orange-600 self-center">{openCRs} open change request{openCRs !== 1 ? 's' : ''} — resolve before accepting</span>
                  )}
                </div>
              )}

              {/* Revisions */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-gray-800">Revisions</h4>
                  <button disabled className="text-xs text-gray-300 cursor-not-allowed" title="Side-by-side compare — coming later">Compare</button>
                </div>
                {slot.revisions.length === 0 ? (
                  <p className="text-xs text-gray-500 italic">No revisions uploaded yet.</p>
                ) : (
                  <div className="space-y-2">
                    {slot.revisions.map((rev) => (
                      <div key={rev.id} className={`border rounded-lg p-3 ${rev.superseded ? 'border-gray-200 bg-gray-50 opacity-70' : 'border-primary-200 bg-primary-50'}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900">
                            R{rev.revision_no}{!rev.superseded && <span className="ml-2 text-[10px] uppercase tracking-wide text-primary-600 font-semibold">Current</span>}
                          </span>
                          {rev.fileUrl && (
                            <a href={rev.fileUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary-600 hover:text-primary-800">View file</a>
                          )}
                        </div>
                        {rev.original_filename && <p className="text-xs text-gray-500 mt-0.5 truncate">{rev.original_filename}</p>}
                        {rev.note && <p className="text-xs text-gray-700 mt-1"><span className="text-gray-400">What changed:</span> {rev.note}</p>}
                        <p className="text-[10px] text-gray-400 mt-1">{rev.uploaded_by} · {fmtDate(rev.uploaded_at)}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload a revision (or Contact Details form) */}
                {isAppointment ? (
                  <p className="text-xs text-gray-400 mt-2">The appointment letter is linked from the Setup tab.</p>
                ) : isContactDetails ? (
                  canEditForm ? (
                    <div className="mt-3 border border-dashed border-gray-300 rounded-lg p-3 space-y-2">
                      <label className="block text-xs font-medium text-gray-700">Contact details</label>
                      {[
                        ['fullName', 'Full name *'],
                        ['position', 'Position *'],
                        ['company', 'Company *'],
                        ['group', 'Group / division'],
                        ['mobile', 'Mobile *'],
                        ['altMobile', 'Alternate mobile'],
                        ['email', 'Email *'],
                      ].map(([key, label]) => (
                        <div key={key}>
                          <label className="block text-[11px] text-gray-500 mb-0.5">{label}</label>
                          <input
                            type={key === 'email' ? 'email' : 'text'}
                            value={contactForm[key]}
                            onChange={(e) => setContactForm((f) => ({ ...f, [key]: e.target.value }))}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </div>
                      ))}
                      <button
                        onClick={doSaveContact}
                        disabled={busy || !contactForm.fullName.trim() || !contactForm.position.trim() || !contactForm.company.trim() || !contactForm.mobile.trim() || !contactForm.email.trim()}
                        className="mt-1 px-3 py-1.5 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                      >
                        {busy ? 'Saving…' : 'Save & generate PDF'}
                      </button>
                      <p className="text-[11px] text-gray-400">Saving creates a new revision and a one-page PDF for the DRE pack.</p>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 mt-2">The contractor completes this form.</p>
                  )
                ) : canUpload ? (
                  <div className="mt-3 border border-dashed border-gray-300 rounded-lg p-3">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {slot.revisions.length === 0 ? 'Upload document for review' : 'Upload a new revision'}
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                      onChange={(e) => setFile(e.target.files?.[0] || null)}
                      className="w-full text-xs text-gray-600 file:mr-2 file:py-1.5 file:px-2.5 file:rounded file:border-0 file:text-xs file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                    />
                    <textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      rows={2}
                      placeholder={slot.revisions.length === 0 ? 'Optional note for the reviewer' : 'Note for reviewer — what changed in this revision'}
                      className="w-full mt-2 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                    />
                    <p className="text-[11px] text-gray-400 mt-1">Accepted: PDF, JPG or PNG (max 25 MB).</p>
                    <button onClick={doUpload} disabled={!file || busy}
                      className="mt-2 px-3 py-1.5 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
                      {busy ? 'Uploading…' : (slot.revisions.length === 0 ? 'Upload for review' : 'Upload revision')}
                    </button>
                  </div>
                ) : null}
              </div>

              {/* Change requests thread */}
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-2">Change requests &amp; comments</h4>
                {slot.changeRequests.length === 0 ? (
                  <p className="text-xs text-gray-500 italic">Nothing raised yet.</p>
                ) : (
                  <div className="space-y-3">
                    {slot.changeRequests.map((cr) => {
                      const st = CR_STATE[cr.state] || CR_STATE.open;
                      return (
                        <div key={cr.id} className="border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-gray-700">{cr.code}</span>
                              <span className="text-[10px] uppercase tracking-wide text-gray-400">{cr.kind === 'comment' ? 'Comment' : 'Change request'}</span>
                            </div>
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium" style={{ backgroundColor: `${st.color}1a`, color: st.color }}>
                              {st.label}
                            </span>
                          </div>
                          <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">{renderWithMentions(cr.body)}</p>
                          {renderAttachments(cr.attachments)}
                          <p className="text-[10px] text-gray-400 mt-1">{cr.created_by} · {fmtDate(cr.created_at)}</p>

                          {/* replies */}
                          {cr.replies?.length > 0 && (
                            <div className="mt-2 space-y-1.5 pl-3 border-l-2 border-gray-100">
                              {cr.replies.map((rep) => (
                                <div key={rep.id}>
                                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{renderWithMentions(rep.body)}</p>
                                  {renderAttachments(rep.attachments)}
                                  <p className="text-[10px] text-gray-400">{rep.created_by} · {fmtDate(rep.created_at)}</p>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* reply composer */}
                          <div className="mt-2 flex gap-1.5">
                            <input
                              type="text"
                              value={replyText[cr.id] || ''}
                              onChange={(e) => setReplyText((p) => ({ ...p, [cr.id]: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === 'Enter') addReply(cr.id); }}
                              placeholder="Reply…"
                              className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                            />
                            <label className="px-2 py-1 text-xs font-medium text-gray-500 border border-gray-200 rounded hover:bg-gray-50 cursor-pointer flex items-center" title="Attach a file (PDF, JPG or PNG, max 25 MB)">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                              <input
                                type="file"
                                multiple
                                accept={ATTACH_ACCEPT}
                                className="hidden"
                                onChange={(e) => {
                                  const picked = Array.from(e.target.files || []);
                                  setReplyFiles((p) => ({ ...p, [cr.id]: [...(p[cr.id] || []), ...picked] }));
                                  e.target.value = '';
                                }}
                              />
                            </label>
                            <button onClick={() => addReply(cr.id)} disabled={busy || !(replyText[cr.id] || '').trim()}
                              className="px-2.5 py-1 text-xs font-medium text-primary-600 border border-primary-200 rounded hover:bg-primary-50 disabled:opacity-40">
                              Send
                            </button>
                          </div>
                          {renderFileChips(replyFiles[cr.id], (i) => setReplyFiles((p) => ({ ...p, [cr.id]: (p[cr.id] || []).filter((_, idx) => idx !== i) })))}

                          {/* state actions */}
                          {cr.kind === 'change_request' && cr.state !== 'verified' && (
                            <div className="mt-2 flex gap-2">
                              {isConsultant && (
                                <button onClick={() => setCrState(cr.id, 'verified')} disabled={busy} className="text-xs text-emerald-600 hover:text-emerald-800 font-medium">Mark verified</button>
                              )}
                              {cr.state === 'open' && (
                                <button onClick={() => setCrState(cr.id, 'addressed')} disabled={busy} className="text-xs text-blue-600 hover:text-blue-800 font-medium">Mark addressed</button>
                              )}
                            </div>
                          )}
                          {isConsultant && cr.state === 'verified' && (
                            <button onClick={() => setCrState(cr.id, 'open')} disabled={busy} className="mt-2 text-xs text-gray-400 hover:text-gray-600">Reopen</button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Raise a change request / comment (consultant) */}
                {isConsultant && (
                  <div className="mt-3 border border-gray-200 rounded-lg p-3">
                    <div className="flex gap-3 mb-2 text-sm">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="radio" checked={crKind === 'change_request'} onChange={() => setCrKind('change_request')} className="text-primary-600 focus:ring-primary-500" />
                        Change request
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="radio" checked={crKind === 'comment'} onChange={() => setCrKind('comment')} className="text-primary-600 focus:ring-primary-500" />
                        Comment
                      </label>
                    </div>
                    <div className="relative">
                      <textarea
                        ref={crInputRef}
                        value={crBody}
                        onChange={handleCrBodyChange}
                        onKeyDown={handleCrKeyDown}
                        rows={2}
                        placeholder={`${crKind === 'change_request' ? 'Describe the change the contractor must make…' : 'Add a comment…'} (type @ to mention someone)`}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                      />
                      {showMention && filteredMentions().length > 0 && (
                        <div className="absolute z-10 left-0 right-0 mt-1 max-h-44 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
                          {filteredMentions().map((u, idx) => (
                            <div
                              key={u.username}
                              onMouseDown={(e) => { e.preventDefault(); insertMention(u); }}
                              onMouseEnter={() => setMentionIndex(idx)}
                              className={`px-3 py-2 cursor-pointer text-sm flex items-center gap-2 ${idx === mentionIndex ? 'bg-amber-50 text-amber-900' : 'hover:bg-gray-50 text-gray-700'}`}
                            >
                              <span className="font-medium">@{u.username}</span>
                              {(u.firstName || u.lastName) && (
                                <span className="text-xs text-gray-400">{`${u.firstName || ''} ${u.lastName || ''}`.trim()}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {renderFileChips(crFiles, (i) => setCrFiles((prev) => prev.filter((_, idx) => idx !== i)))}
                    <div className="mt-2 flex items-center gap-2">
                      <button onClick={addCR} disabled={busy || !crBody.trim()}
                        className="px-3 py-1.5 text-sm font-medium bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50">
                        {crKind === 'change_request' ? 'Request changes' : 'Add comment'}
                      </button>
                      <label className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer" title="Attach files (PDF, JPG or PNG, max 25 MB each)">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                        Attach
                        <input
                          type="file"
                          multiple
                          accept={ATTACH_ACCEPT}
                          className="hidden"
                          onChange={(e) => {
                            const picked = Array.from(e.target.files || []);
                            setCrFiles((prev) => [...prev, ...picked]);
                            e.target.value = '';
                          }}
                        />
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default WayleaveDocDrawer;
